import axiosInstance from '@/configs/axiosConfig';
import type {
  AgentOrderableItem,
  AgentOrderReview,
  AgentSettings,
  AgentTurn,
  AgentTurnFilters,
  AmountDifference,
  AssignDeliveryGroupRequest,
  Country,
  CreateCustomerResponse,
  CreateDeliveryGroupRequest,
  CreateDriverRequest,
  CloseOrderRequest,
  CreateKnowledgeBaseEntryRequest,
  CreateKnowledgeBaseEntryResponse,
  CreateOrderRequest,
  CreatePaymentMethodRequest,
  CreateTruckRequest,
  Customer,
  DashboardTotalsResponse,
  DataCleansingCheck,
  DataCleansingRow,
  DeliverPayLaterRequest,
  DeliveryGroup,
  DeliveryGroupAddress,
  Driver,
  GenerateRouteRequest,
  GoogleContact,
  GroupNextDeliveryDateResponse,
  ItemBottleValue,
  KnowledgeBasePage,
  LinkReceiptInboxCustomerRequest,
  LoyverseCustomer,
  LoyverseItem,
  NextDeliveryDateResponse,
  Order,
  OrderStatus,
  OwingCustomer,
  Page,
  Payment,
  PaymentMethod,
  PickReceiptInboxAddressRequest,
  PlanningSettings,
  ReceiptInboxPageResponse,
  ReceiptInboxStatus,
  RecordPaymentRequest,
  RegisterCustomerRequest,
  RejectReceiptInboxRequest,
  ReopenOrderRequest,
  ReorderKnowledgeBaseRequest,
  ReviewAmountDifferenceRequest,
  Route,
  SetItemBottleValueRequest,
  SetSubscriptionEndDateRequest,
  SubscriptionListRow,
  SubscriptionStatusFilter,
  SubscriptionType,
  Truck,
  UnassignedDeliveryAddress,
  UpdateAgentSettingsRequest,
  UpdateCustomerRequest,
  UpdateDeliveryAddressRequest,
  UpdateDeliveryGroupRequest,
  UpdateDriverRequest,
  UpdateInvoiceAccountRequest,
  UpdateKnowledgeBaseEntryRequest,
  UpdateOrderRequest,
  UpdatePaymentMethodRequest,
  UpdatePlanningSettingsRequest,
  UpdateTruckRequest,
  WeeklyMessageSend
} from '@/types/types';
import type { AxiosResponse } from 'axios';
import { HttpStatusCode, isAxiosError } from 'axios';

/* REQUEST METHODS */
const get = async <RESPONSE>(url: string): Promise<RESPONSE> => {
  const response = await axiosInstance.get<RESPONSE>(url);
  return response.data;
};

/* Used for searching with sensitive data */
const getWithPost = async <RESPONSE, ENTITY>(url: string, entity: ENTITY): Promise<RESPONSE> => {
  const response = await axiosInstance.post<RESPONSE, AxiosResponse<RESPONSE>, ENTITY>(url, entity);
  return response.data;
};

const create = async <RESPONSE, ENTITY>(url: string, entity: ENTITY): Promise<RESPONSE> => {
  const response = await axiosInstance.post<RESPONSE, AxiosResponse<RESPONSE>, ENTITY>(url, entity);
  return response.data;
};

const update = async <RESPONSE, ENTITY>(url: string, entity: ENTITY): Promise<RESPONSE> => {
  const response = await axiosInstance.put<RESPONSE, AxiosResponse<RESPONSE>, ENTITY>(url, entity);
  return response.data;
};

const remove = async <RESPONSE>(url: string): Promise<RESPONSE> => {
  const response = await axiosInstance.delete<RESPONSE>(url);
  return response.data;
};

/* CUSTOMERS */
// REQ-1/REQ-2/REQ-3/REQ-4/REQ-5/REQ-7: every customer endpoint moved under `/api/v1/customers`
// (apps/api/README.md § API Conventions). Every mutation but create answers 204 — this module
// returns `void` for them, and the caller re-GETs (customer detail or list) to see the result.
export const customerApi = {
  // REQ-7: `active` (owner decision 2026-10-24) -- omitted means every customer, `true` means at
  // least one `active=true` subscription, `false` means none -- combinable with `search` and
  // paging, so the Customers page's "Show inactive customers" toggle works across every customer
  // again, not just the current page. `sort` (owner decision 2026-10-24, SHARED_CONTRACTS round 2
  // "Sorting") is always sent, carrying either the column the user clicked or the page's own
  // default -- `createdAt,desc`, matching `CustomerController`'s own default exactly.
  getCustomers: (page: number, size: number, search?: string, active?: boolean, sort?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search) params.set('search', search);
    if (active !== undefined) params.set('active', String(active));
    if (sort) params.set('sort', sort);
    return get<Page<Customer>>(`api/v1/customers?${params.toString()}`);
  },
  getCustomer: (id: string) => get<Customer>(`api/v1/customers/${id}`),
  createCustomer: (customer: RegisterCustomerRequest) =>
    create<CreateCustomerResponse, RegisterCustomerRequest>('api/v1/customers', customer),
  updateCustomer: (id: string, customer: UpdateCustomerRequest) =>
    update<void, UpdateCustomerRequest>(`api/v1/customers/${id}`, customer),
  deleteCustomer: (id: string) => remove<void>(`api/v1/customers/${id}`),
  updateInvoiceAccount: (customerId: string, invoiceAccount: UpdateInvoiceAccountRequest) =>
    create<void, UpdateInvoiceAccountRequest>(
      `api/v1/customers/${customerId}/invoice-account`,
      invoiceAccount
    ),
  deleteInvoiceAccount: (customerId: string) =>
    remove<void>(`api/v1/customers/${customerId}/invoice-account`),
  updateDeliveryAddress: (customerId: string, deliveryAddress: UpdateDeliveryAddressRequest) =>
    create<void, UpdateDeliveryAddressRequest>(
      `api/v1/customers/${customerId}/delivery-address`,
      deliveryAddress
    ),
  deleteDeliveryAddress: (customerId: string, deliveryAddressId: string) =>
    remove<void>(`api/v1/customers/${customerId}/delivery-address/${deliveryAddressId}`)
};

/* ORDERS */
// REQ-1/REQ-2/REQ-3/REQ-4: every order endpoint moved under `/api/v1/orders`, except the receipt
// webhook (apps/api/README.md § API Conventions). `status` is sent as a repeated query param, not
// comma-joined — `URLSearchParams.append` per value, matching the server's `@RequestParam
// List<Status>`.
export const orderApi = {
  // REQ-7: `search` (owner decision 2026-10-24) matches the order's customer first name, last
  // name or phone number, each independently, case-insensitive -- not street, city, receipt
  // number or item names (see OrdersPage's own Unproven note). `sort` (owner decision 2026-10-24,
  // SHARED_CONTRACTS round 2 "Sorting") is always sent, carrying either the column the user
  // clicked or the page's own default -- `orderDate,desc`, matching `OrderController`'s own
  // default exactly.
  getOrders: (
    status: OrderStatus[] | undefined,
    page: number,
    size: number,
    search?: string,
    sort?: string
  ) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    status?.forEach(s => params.append('status', s));
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    return get<Page<Order>>(`api/v1/orders?${params.toString()}`);
  },
  getOrder: (id: string) => get<Order>(`api/v1/orders/${id}`),
  createOrder: (order: CreateOrderRequest) =>
    create<{ id: string }, CreateOrderRequest>('api/v1/orders', order),
  updateOrder: (id: string, order: UpdateOrderRequest) =>
    update<void, UpdateOrderRequest>(`api/v1/orders/${id}`, order),
  cancelOrder: (id: string) => remove<void>(`api/v1/orders/${id}`),
  // REQ-9: undelivered orders past their delivery date — no job moves them forward any more, so
  // they stay here (with their original date) until staff reschedule them.
  // Paged on the server, oldest delivery date first (the API adds `id` as a tiebreaker).
  getMissedOrders: (page: number, size: number) =>
    get<Page<Order>>(`api/v1/orders/missed?page=${page}&size=${size}&sort=deliveryDate,asc`),
  // REQ-41: mark a planned order delivered, pay later -- 204; 409 when the status no longer
  // allows it, a generic water line is still unresolved, or an item has no fixed Loyverse price.
  deliverPayLater: (id: string, request: DeliverPayLaterRequest) =>
    create<void, DeliverPayLaterRequest>(`api/v1/orders/${id}/deliver-pay-later`, request),
  // REQ-43: close/reopen a pay-later order -- both 204; 409 on a stale version or a status that
  // does not allow the action (apps/api/README.md § Payments).
  closeOrder: (id: string, request: CloseOrderRequest) =>
    create<void, CloseOrderRequest>(`api/v1/orders/${id}/close`, request),
  reopenOrder: (id: string, request: ReopenOrderRequest) =>
    create<void, ReopenOrderRequest>(`api/v1/orders/${id}/reopen`, request),
  // REQ-44/REQ-45: the unpaid and partly paid orders by customer, by name or narrowed to one
  // customer -- `customerId` omitted lists every owing customer (the Payments page, a later
  // bundle); given, the page holds only that customer, or is empty when they owe nothing.
  getOwingOrders: (page: number, size: number, customerId?: string) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (customerId) params.set('customerId', customerId);
    return get<Page<OwingCustomer>>(`api/v1/orders/owing?${params.toString()}`);
  },
  // REQ-42/REQ-45: the Payments page's "amount differs" review list -- unreviewed only, sorted by
  // delivery date (the controller's own default); staff mark one reviewed with the version they
  // read (apps/api/README.md § Payments).
  getAmountDifferences: (page: number, size: number) =>
    get<Page<AmountDifference>>(`api/v1/orders/amount-differences?page=${page}&size=${size}`),
  reviewAmountDifference: (id: string, request: ReviewAmountDifferenceRequest) =>
    create<void, ReviewAmountDifferenceRequest>(
      `api/v1/orders/${id}/review-amount-difference`,
      request
    )
};

/* PAYMENT METHODS */
// REQ-40: the editable list staff pick a method from (apps/api/README.md § Payments) -- always
// read a page at a time, sorted by `position` (the controller's own default), inactive methods
// included so a payment already recorded with one still shows its name.
export const paymentMethodApi = {
  getPaymentMethods: (page: number, size: number) =>
    get<Page<PaymentMethod>>(`api/v1/payment-methods?page=${page}&size=${size}`),
  createPaymentMethod: (request: CreatePaymentMethodRequest) =>
    create<void, CreatePaymentMethodRequest>('api/v1/payment-methods', request),
  updatePaymentMethod: (id: string, request: UpdatePaymentMethodRequest) =>
    update<void, UpdatePaymentMethodRequest>(`api/v1/payment-methods/${id}`, request)
};

/* PAYMENTS */
// REQ-40: record, list and delete payments -- `getPayments` takes at most one of `customerId`/
// `orderId` (apps/api answers 400 when both are given), newest `paymentDate` first (the
// controller's own default sort, so this app never overrides it).
export const paymentApi = {
  getPayments: (
    filter: { customerId?: string | undefined; orderId?: string | undefined },
    page: number,
    size: number
  ) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (filter.customerId) params.set('customerId', filter.customerId);
    if (filter.orderId) params.set('orderId', filter.orderId);
    return get<Page<Payment>>(`api/v1/payments?${params.toString()}`);
  },
  recordPayment: (request: RecordPaymentRequest) =>
    create<void, RecordPaymentRequest>('api/v1/payments', request),
  deletePayment: (id: string) => remove<void>(`api/v1/payments/${id}`)
};

/* LOYVERSE */
// REQ-1/REQ-3: moved under `/api/v1`, both now paged (apps/api/README.md § API Conventions).
export const loyverseApi = {
  getItems: (page: number, size: number) =>
    get<Page<LoyverseItem>>(`api/v1/loyverse/items?page=${page}&size=${size}&sort=itemName,asc`),
  getCustomers: (name: string | undefined, page: number, size: number) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (name) params.set('name', name);
    return get<Page<LoyverseCustomer>>(`api/v1/loyverse/customers?${params.toString()}`);
  }
};

/* GOOGLE CONTACTS */
// REQ-1/REQ-3: moved under `/api/v1`, now paged (apps/api/README.md § API Conventions); the body
// (the search name) is unchanged.
export const googleContactApi = {
  searchContacts: (name: string, page: number, size: number) =>
    getWithPost<Page<GoogleContact>, string>(
      `api/v1/google-contacts?page=${page}&size=${size}`,
      name
    )
};

/* BASIC DATA */
export const basicDataApi = {
  getCountries: () => get<Country[]>('api/v1/basic-data/countries')
};

/* DELIVERY GROUPS */
export const deliveryGroupApi = {
  getDeliveryGroups: (page: number, size: number) =>
    get<Page<DeliveryGroup>>(`api/v1/delivery-groups?page=${page}&size=${size}`),
  createDeliveryGroup: (group: CreateDeliveryGroupRequest) =>
    create<void, CreateDeliveryGroupRequest>('api/v1/delivery-groups', group),
  updateDeliveryGroup: (id: string, group: UpdateDeliveryGroupRequest) =>
    update<void, UpdateDeliveryGroupRequest>(`api/v1/delivery-groups/${id}`, group),
  deleteDeliveryGroup: (id: string) => remove<void>(`api/v1/delivery-groups/${id}`),
  // REQ-3/REQ-4: a group's own next delivery date, used to pre-fill an order as soon as the group
  // is picked inline for an address that has none yet — before that address is assigned to it.
  getNextDeliveryDate: (id: string) =>
    get<GroupNextDeliveryDateResponse>(`api/v1/delivery-groups/${id}/next-delivery-date`),
  // REQ-23: a group's addresses in priority order, for placing a new one between its neighbours —
  // size defaults to the backend's own clamp of 100, large enough that every group in practice
  // fits on the one page (bulk assignment, and so a group with hundreds of stops, are out of
  // scope — docs/requirements.md Out of scope).
  getDeliveryGroupAddresses: (id: string, page = 0, size = 100) =>
    get<Page<DeliveryGroupAddress>>(
      `api/v1/delivery-groups/${id}/addresses?page=${page}&size=${size}`
    )
};

/* WEEKLY GROUP MESSAGE */
// REQ-13: a delivery group's own weekly-message send history, nested under its parent resource —
// the default sort (`sentAt,desc`, apps/api's own controller default) is never overridden here.
export const weeklyMessageSendApi = {
  getWeeklyMessageSends: (deliveryGroupId: string, page: number, size: number) =>
    get<Page<WeeklyMessageSend>>(
      `api/v1/delivery-groups/${deliveryGroupId}/weekly-message-sends?page=${page}&size=${size}`
    )
};

/* TRUCKS */
export const truckApi = {
  getTrucks: (page: number, size: number) =>
    get<Page<Truck>>(`api/v1/trucks?page=${page}&size=${size}`),
  createTruck: (truck: CreateTruckRequest) =>
    create<void, CreateTruckRequest>('api/v1/trucks', truck),
  updateTruck: (id: string, truck: UpdateTruckRequest) =>
    update<void, UpdateTruckRequest>(`api/v1/trucks/${id}`, truck),
  deleteTruck: (id: string) => remove<void>(`api/v1/trucks/${id}`)
};

/* PLANNING SETTINGS */
export const planningSettingsApi = {
  getSettings: () => get<PlanningSettings>('api/v1/planning-settings'),
  updateSettings: (settings: UpdatePlanningSettingsRequest) =>
    update<void, UpdatePlanningSettingsRequest>('api/v1/planning-settings', settings)
};

/* DRIVERS */
export const driverApi = {
  getDrivers: (page: number, size: number) =>
    get<Page<Driver>>(`api/v1/drivers?page=${page}&size=${size}`),
  createDriver: (driver: CreateDriverRequest) =>
    create<void, CreateDriverRequest>('api/v1/drivers', driver),
  updateDriver: (id: string, driver: UpdateDriverRequest) =>
    update<void, UpdateDriverRequest>(`api/v1/drivers/${id}`, driver),
  deleteDriver: (id: string) => remove<void>(`api/v1/drivers/${id}`)
};

/* ITEM BOTTLE VALUES */
export const itemBottleValueApi = {
  getItemBottleValues: (page: number, size: number) =>
    get<Page<ItemBottleValue>>(
      `api/v1/item-bottle-values?page=${page}&size=${size}&sort=itemName,asc`
    ),
  setItemBottleValue: (itemId: string, request: SetItemBottleValueRequest) =>
    update<void, SetItemBottleValueRequest>(`api/v1/item-bottle-values/${itemId}`, request)
};

/* ROUTE PLANNING */
export const routeApi = {
  // REQ-13: `GET /api/v1/routes/{date}` 404s when nothing has been generated yet for that date —
  // read as `null`, an empty state the route-planning page shows distinctly from a real error
  // (react-base/RULES.md § Query-state rendering), not re-thrown.
  getRoute: (date: string) =>
    axiosInstance
      .get<Route>(`api/v1/routes/${date}`)
      .then(response => response.data)
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) return null;
        throw error;
      }),
  generateRoute: (date: string, request: GenerateRouteRequest) =>
    create<void, GenerateRouteRequest>(`api/v1/routes/${date}/generate`, request),
  // REQ-8/REQ-39: pulling a late or off-day ("not a delivery day") order in, "Add to this day".
  // No request body — the actor and the instant are read server-side (apps/api's
  // RouteService#includeOrder). The deprecated `late-orders/{orderId}/include` path still works;
  // this app uses the new one (SHARED_CONTRACTS, M14 apps/api bundle).
  includeOrder: (date: string, orderId: string) =>
    create<void, undefined>(`api/v1/routes/${date}/orders/${orderId}/include`, undefined)
};

/* RECEIPT INBOX */
export const receiptInboxApi = {
  // REQ-7: one all-receipts list, filterable by status — `status` omitted reads every status
  // (apps/api's ReceiptInboxController). Sent as a repeated query param, not comma-joined —
  // `URLSearchParams.append` per value, matching the server's `@RequestParam List<Status>`, so
  // the Pending default sends both its statuses in one call and gets back one page over their
  // union. `needsAttentionCount` on every response is the whole inbox's own count, independent of
  // which status filter this particular call used, so the badge in use-receipt-inbox.ts never
  // flickers when staff switch filters.
  getReceiptInbox: (status: ReceiptInboxStatus[] | undefined, page: number, size: number) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    status?.forEach(s => params.append('status', s));
    params.set('sort', 'receiptCreatedAt,desc');
    return get<ReceiptInboxPageResponse>(`api/v1/receipt-inbox?${params.toString()}`);
  },
  // REQ-5: links an unknown customer to an inbox item before any decision.
  linkCustomer: (id: string, request: LinkReceiptInboxCustomerRequest) =>
    update<void, LinkReceiptInboxCustomerRequest>(`api/v1/receipt-inbox/${id}/customer`, request),
  // REQ-5: picks one of a known customer's several delivery addresses; applies nothing itself —
  // apps/api returns the item to RECEIVED for the worker's next tick
  // (docs/receipt-inbox-state.puml RI8).
  pickAddress: (id: string, request: PickReceiptInboxAddressRequest) =>
    update<void, PickReceiptInboxAddressRequest>(`api/v1/receipt-inbox/${id}/address`, request),
  // REQ-4: applies the planned subscription change once, at the order the worker already created
  // and completed — no body any more: the address was resolved before the item ever reached
  // WAITING_FOR_CONFIRMATION.
  confirm: (id: string) => create<void, undefined>(`api/v1/receipt-inbox/${id}/confirm`, undefined),
  // REQ-4/REQ-5: rejects the item with a reason; changes no subscription.
  reject: (id: string, request: RejectReceiptInboxRequest) =>
    create<void, RejectReceiptInboxRequest>(`api/v1/receipt-inbox/${id}/reject`, request),
  // REQ-7: staff dismiss the changed mark — the currently-applied content is unchanged (RC4).
  dismissChange: (id: string) =>
    create<void, undefined>(`api/v1/receipt-inbox/${id}/dismiss`, undefined),
  // REQ-7: staff replace the changed content — corrects the order's total and lines only, never
  // the subscription (RC5); apps/api refuses this (409) when no order holds the number yet.
  replaceChange: (id: string) =>
    create<void, undefined>(`api/v1/receipt-inbox/${id}/replace`, undefined)
};

/* DELIVERY ADDRESSES (group assignment) */
export const deliveryAddressGroupApi = {
  getUnassigned: (page: number, size: number) =>
    get<Page<UnassignedDeliveryAddress>>(
      `api/v1/delivery-addresses/unassigned?page=${page}&size=${size}`
    ),
  assignGroup: (addressId: string, request: AssignDeliveryGroupRequest) =>
    update<void, AssignDeliveryGroupRequest>(
      `api/v1/delivery-addresses/${addressId}/group`,
      request
    ),
  getNextDeliveryDate: (addressId: string) =>
    get<NextDeliveryDateResponse>(`api/v1/delivery-addresses/${addressId}/next-delivery-date`)
};

/* SUBSCRIPTIONS */
export const subscriptionApi = {
  // REQ-9/REQ-10: the administrator's one subscription write.
  setEndDate: (id: string, request: SetSubscriptionEndDateRequest) =>
    update<void, SetSubscriptionEndDateRequest>(`api/v1/subscriptions/${id}/end-date`, request),
  // REQ-8: replaces the Subscriptions page's own all-customers download (apps/api/README.md §
  // API Conventions). `search` matches first name, last name, street or city independently.
  // `sort` (owner decision 2026-10-24, SHARED_CONTRACTS round 2 "Sorting") is always sent,
  // carrying either the column the user clicked or the page's own default -- `endDate,asc`,
  // matching `SubscriptionController`'s own default exactly.
  getSubscriptions: (
    page: number,
    size: number,
    options: {
      type?: SubscriptionType;
      search?: string;
      status?: SubscriptionStatusFilter;
      sort?: string;
    } = {}
  ) => {
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (options.type) params.set('type', options.type);
    if (options.search) params.set('search', options.search);
    if (options.status) params.set('status', options.status);
    if (options.sort) params.set('sort', options.sort);
    return get<Page<SubscriptionListRow>>(`api/v1/subscriptions?${params.toString()}`);
  },
  // REQ-8: replaces the Dashboard's own client-computed "ending soon" list.
  getUpcomingActions: (page: number, size: number) =>
    get<Page<SubscriptionListRow>>(
      `api/v1/subscriptions/upcoming-actions?page=${page}&size=${size}`
    )
};

/* MAP PINS (Customer Map) */
export const mapPinApi = {
  // REQ-1/REQ-8: replaces the Customer Map's own all-customers download — one page of delivery
  // addresses with coordinates (apps/api/README.md § API Conventions).
  getMapPins: (page: number, size: number) =>
    get<Page<UnassignedDeliveryAddress>>(
      `api/v1/delivery-addresses/map-pins?page=${page}&size=${size}`
    )
};

/* DASHBOARD */
export const dashboardApi = {
  // REQ-8: replaces the Dashboard's own all-customers download — a single, unpaged totals object
  // (apps/api/README.md § API Conventions).
  getTotals: () => get<DashboardTotalsResponse>('api/v1/dashboard/totals')
};

/* AGENT ORDERABLE ITEMS */
// REQ-7: which Loyverse items the ordering agent may order. `markOrderable`/`unmarkOrderable` are
// both idempotent 204s (apps/api's AgentOrderableItemController) -- the Items page re-GETs the
// page to see the result, same as every other mutation in this module.
export const agentOrderableItemApi = {
  getAgentOrderableItems: (page: number, size: number) =>
    get<Page<AgentOrderableItem>>(
      `api/v1/agent-orderable-items?page=${page}&size=${size}&sort=itemName,asc`
    ),
  markOrderable: (itemId: string) =>
    update<void, undefined>(`api/v1/agent-orderable-items/${itemId}`, undefined),
  unmarkOrderable: (itemId: string) => remove<void>(`api/v1/agent-orderable-items/${itemId}`)
};

/* AGENT LOG */
// REQ-2/REQ-17: the Agent log page's own list (every filter independently optional) and the
// retry of a failed turn (apps/api/README.md § Agent Log). `sort` is always `createdAt,desc`,
// the controller's own default -- this list is never column-sorted by staff, unlike
// Orders/Customers/Subscriptions (owner decision: turns read newest-first only).
export const agentTurnApi = {
  getAgentTurns: (filters: AgentTurnFilters, page: number, size: number) => {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort: 'createdAt,desc'
    });
    if (filters.conversationId !== undefined) {
      params.set('conversationId', String(filters.conversationId));
    }
    if (filters.orderId) params.set('orderId', filters.orderId);
    if (filters.decision) params.set('decision', filters.decision);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    return get<Page<AgentTurn>>(`api/v1/agent-turns?${params.toString()}`);
  },
  // REQ-17: 204, or a 409 this app never expects to hit in practice (a double-click would need to
  // win a race against the dialog's own disabled-while-pending button) -- apps/api's own 409 on a
  // non-FAILED turn.
  retryAgentTurn: (id: string) =>
    create<void, undefined>(`api/v1/agent-turns/${id}/retry`, undefined)
};

/* AGENT SETTINGS */
// REQ-6/REQ-16: the agent's mode and OpenRouter model, a singleton apps/api seeds itself (shadow,
// no model). The PUT is a full replace and answers 204 (apps/api/README.md § AI Agent).
export const agentSettingsApi = {
  getSettings: () => get<AgentSettings>('api/v1/agent-settings'),
  updateSettings: (request: UpdateAgentSettingsRequest) =>
    update<void, UpdateAgentSettingsRequest>('api/v1/agent-settings', request)
};

/* KNOWLEDGE BASE */
// REQ-26/REQ-27: the entries staff keep for the agent (apps/api/README.md § AI Agent), always in
// position order, so the list takes no `sort`; `size` is capped at 100. Create answers 201 with the
// new id and the other writes 204, so the page re-GETs the list to see the result. Enabling and
// disabling is `updateEntry` with `enabled` flipped. `reorderEntries` sets the whole order in one
// call and takes every entry's id.
export const knowledgeBaseApi = {
  getEntries: (page: number, size: number) =>
    get<KnowledgeBasePage>(`api/v1/knowledge-base-entries?page=${page}&size=${size}`),
  createEntry: (request: CreateKnowledgeBaseEntryRequest) =>
    create<CreateKnowledgeBaseEntryResponse, CreateKnowledgeBaseEntryRequest>(
      'api/v1/knowledge-base-entries',
      request
    ),
  updateEntry: (id: string, request: UpdateKnowledgeBaseEntryRequest) =>
    update<void, UpdateKnowledgeBaseEntryRequest>(`api/v1/knowledge-base-entries/${id}`, request),
  deleteEntry: (id: string) => remove<void>(`api/v1/knowledge-base-entries/${id}`),
  reorderEntries: (request: ReorderKnowledgeBaseRequest) =>
    update<void, ReorderKnowledgeBaseRequest>('api/v1/knowledge-base-entries/order', request)
};

/* AGENT ORDERS */
// REQ-9: the review queue -- every agent-created order staff have not approved yet, with its
// conversation link, oldest delivery date first (apps/api's own default sort). Correcting and
// cancelling reuse the existing `orderApi.updateOrder`/`cancelOrder` calls above (apps/api/README.md:
// "reuse the existing, generic OrderService/OrderController paths").
export const agentOrderApi = {
  getAwaitingReview: (page: number, size: number) =>
    get<Page<AgentOrderReview>>(`api/v1/agent/orders/awaiting-review?page=${page}&size=${size}`),
  // REQ-9: 204 on success; 409 (ProblemDetail, a human-readable sentence) when a generic water
  // line is still unresolved, the order is already approved, or its status no longer allows it.
  approve: (id: string) => create<void, undefined>(`api/v1/agent/orders/${id}/approve`, undefined)
};

/* DATA CLEANSING */
// REQ-20/REQ-33: read-only. The checks come from the API with their name and count, so a check
// added there needs no change here; `key` is the stable slug in the rows path, URL-encoded because
// the Data cleansing route puts it in the address bar. Both lists are sorted by the only field the
// API accepts (checks by name, rows by the customer's name), sent explicitly so the order never
// depends on a server default.
export const dataCleansingApi = {
  getChecks: (page: number, size: number) =>
    get<Page<DataCleansingCheck>>(
      `api/v1/data-cleansing/checks?page=${page}&size=${size}&sort=name,asc`
    ),
  getRows: (key: string, page: number, size: number) =>
    get<Page<DataCleansingRow>>(
      `api/v1/data-cleansing/checks/${encodeURIComponent(key)}/rows?page=${page}&size=${size}&sort=customerName,asc`
    )
};
